// components/popup/SuspectPopup.tsx
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { PersonSearchPopup } from "./PersonSearchPopup";
import { fetchNotInCustodyReasons } from "services/assetRecoveryService";
import { Person } from "types";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";

interface SuspectPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onInputChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  onSearchPerson: (e: React.FormEvent) => void;
  onPersonSelect: (personId: string) => void;
  onUserSubmit: (data: any, isEditMode: boolean) => void;

  formData: any;
  loading: boolean;
  error: string | null;
  searchFayda: string;
  setSearchFayda: (value: string) => void;
  searchPhone: string;
  setSearchPhone: (value: string) => void;
  searchedPersons: any[];
  selectedPerson: any;
  selectedPersonId: string;
  searchLoading: boolean;
  userFormLoading: boolean;
  suspectFile: File[];
  courtBailFile: File[];
  fileError: string;
  setCourtBailFile: (file: File[]) => void;
  setPoliceBailFile: (file: File[]) => void;

  isEditMode?: boolean;
  existingData?: any;
  setSelectedPerson?: Dispatch<SetStateAction<Person | null>>;
  setSelectedPersonId?: Dispatch<SetStateAction<string>>;
  setSearchedPersons?: Dispatch<SetStateAction<Person[]>>;
}

export const SuspectPopup: React.FC<SuspectPopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onInputChange,
  onSearchPerson,
  onPersonSelect,
  onUserSubmit,

  formData,
  loading,
  error,
  searchFayda,
  setSearchFayda,
  searchPhone,
  setSearchPhone,
  searchedPersons,
  selectedPerson,
  selectedPersonId,
  searchLoading,
  userFormLoading,
  suspectFile,
  courtBailFile,
  setCourtBailFile,
  setPoliceBailFile,
  isEditMode = false,
  existingData,
  setSearchedPersons,
  setSelectedPerson,
  setSelectedPersonId,
}) => {
  const [notInCustodyReasons, setNotInCustodyReasons] = useState<
    { id: string; name: string }[]
  >([]);
  const [showBailInfo, setShowBailInfo] = useState(
    isEditMode && existingData?.bail_information ? true : false
  );

  useEffect(() => {
    fetchNotInCustodyReasons()
      .then(setNotInCustodyReasons)
      .catch(console.error);
  }, []);

  // Auto-show bail info if editing and bail information exists
  useEffect(() => {
    if (isEditMode && existingData?.bail_information) {
      setShowBailInfo(true);
    }
  }, [isEditMode, existingData]);

  const validateForm = () => {
    if (formData.suspect_condition === "not_in_custody" && showBailInfo) {
      if (!formData.bail_information) {
        return "Bail information is required when suspect is not in custody.";
      }
      if (
        formData.bail_information === "court_bail" &&
        !formData.court_bail_amount
      ) {
        return "Court bail amount is required.";
      }
      if (
        formData.bail_information === "police_bail" &&
        !formData.police_bail_amount
      ) {
        return "Police bail amount is required.";
      }
    }
    return "";
  };

  return (
    <PersonSearchPopup
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      onInputChange={onInputChange}
      onSearchPerson={onSearchPerson}
      onPersonSelect={onPersonSelect}
      onUserSubmit={onUserSubmit}
      formData={formData}
      loading={loading}
      error={error}
      searchFayda={searchFayda}
      setSearchFayda={setSearchFayda}
      searchPhone={searchPhone}
      setSearchPhone={setSearchPhone}
      searchedPersons={searchedPersons}
      selectedPerson={selectedPerson}
      selectedPersonId={selectedPersonId}
      searchLoading={searchLoading}
      userFormLoading={userFormLoading}
      title={isEditMode ? "Edit Suspect" : "Add Suspect"}
      description={
        isEditMode ? "Edit suspect information" : "Add suspect information"
      }
      validateForm={validateForm}
      isEditMode={isEditMode}
      existingData={existingData}
      setSearchedPersons={setSearchedPersons}
      setSelectedPerson={setSelectedPerson}
      setSelectedPersonId={setSelectedPersonId}
    >
      {/* Suspect Condition */}
      <div className="flex flex-col">
        <label className="referral-popup-label">Suspect Condition</label>
        <select
          className="referral-form-field"
          onChange={onInputChange}
          name="suspect_condition"
          value={formData.suspect_condition || ""}
        >
          <option value="">Select Suspect Condition</option>
          <option value="judicial_custody">Judicial Custody</option>
          <option value="not_in_custody">Not In Custody</option>
          <option value="police_custody">Police Custody</option>
        </select>
      </div>

      {/* Court Appointment Date only if Police Custody */}
      {formData.suspect_condition === "police_custody" && (
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Court Appointment Date</label>
          <input
            type="date"
            className="referral-form-field"
            onChange={onInputChange}
            name="court_appointment_date"
            value={formData.court_appointment_date || ""}
          />
        </div>
      )}

      {/* Final Verdict Amount only if Judicial Custody */}
      {formData.suspect_condition === "judicial_custody" && (
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Final Verdict Amount</label>
          <input
            type="number"
            className="referral-form-field"
            onChange={onInputChange}
            name="final_verdict_amount"
            placeholder="Final Verdict Amount"
            value={formData.final_verdict_amount || ""}
            step="0.01"
          />
        </div>
      )}

      {/* Not In Custody Reason */}
      {formData.suspect_condition === "not_in_custody" && (
        <>
          <div className="flex flex-col mt-4">
            <label className="referral-popup-label">
              Not In Custody Reason
            </label>
            <select
              className="referral-form-field"
              onChange={onInputChange}
              name="not_in_custody_reason"
              value={formData.not_in_custody_reason || ""}
            >
              <option value="" disabled>
                Select Reason
              </option>
              {notInCustodyReasons?.map((reason) => (
                <option key={reason.id} value={reason.id}>
                  {reason.name}
                </option>
              ))}
            </select>
          </div>

          {/* Checkbox to decide whether to show bail info */}
          <div className="flex items-center mt-4">
            <input
              type="checkbox"
              id="showBailInfo"
              checked={showBailInfo}
              onChange={(e) => setShowBailInfo(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="showBailInfo" className="text-sm text-gray-700">
              Register Bail Information
            </label>
          </div>
        </>
      )}

      {/* Bail Info – Only if Not In Custody and user checked the box */}
      {formData.suspect_condition === "not_in_custody" && showBailInfo && (
        <>
          <div className="flex flex-col mt-4">
            <label className="referral-popup-label">Bail Information</label>
            <select
              className="referral-form-field"
              onChange={onInputChange}
              name="bail_information"
              value={formData.bail_information || ""}
            >
              <option value="">Select Bail Type</option>
              <option value="court_bail">Court Bail</option>
              <option value="police_bail">Police Bail</option>
            </select>
          </div>

          {/* Court Bail */}
          {formData.bail_information === "court_bail" && (
            <>
              <div className="flex flex-col mt-4">
                <label className="referral-popup-label">
                  Court Registration Number
                </label>
                <input
                  type="text"
                  className="referral-form-field"
                  onChange={onInputChange}
                  name="court_registration_number"
                  placeholder="Court Registration Number"
                  value={formData.court_registration_number || ""}
                />
              </div>
              <div className="flex flex-col mt-4">
                <label className="referral-popup-label">
                  Court Bail Amount
                </label>
                <input
                  type="number"
                  className="referral-form-field"
                  onChange={onInputChange}
                  name="court_bail_amount"
                  placeholder="Court Bail Amount"
                  value={formData.court_bail_amount || ""}
                  step="0.01"
                />
              </div>

              <MultipleFileUploadComponent
                uploadedFiles={courtBailFile}
                onFilesChange={setCourtBailFile}
                label="Court Bail Evidence"
              />
            </>
          )}

          {/* Police Bail */}
          {formData.bail_information === "police_bail" && (
            <>
              <div className="flex flex-col mt-4">
                <label className="referral-popup-label">
                  Incident Registration Number
                </label>
                <input
                  type="text"
                  className="referral-form-field"
                  onChange={onInputChange}
                  name="incident_registration_number"
                  placeholder="Incident Registration Number"
                  value={formData.incident_registration_number || ""}
                />
              </div>
              <div className="flex flex-col">
                <label className="referral-popup-label">
                  Police Bail Amount
                </label>
                <input
                  type="number"
                  className="referral-form-field"
                  onChange={onInputChange}
                  name="police_bail_amount"
                  placeholder="Police Bail Amount"
                  value={formData.police_bail_amount || ""}
                  step="0.01"
                />
              </div>
              <div className="flex flex-col">
                {/* <label className="referral-popup-label">
                  Police Bail Evidence
                </label> */}

                <MultipleFileUploadComponent
                  uploadedFiles={suspectFile}
                  onFilesChange={setPoliceBailFile}
                  label="Police Bail Evidence "
                />
              </div>
            </>
          )}
        </>
      )}
    </PersonSearchPopup>
  );
};
